﻿// 🔗 PO editing (v1) — dropdown cell for changing the PO matched to an invoice line.
// Self-contained; rendered only when PO editing is enabled for the space.

import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { Callout, DirectionalHint, Spinner, SpinnerSize } from "@fluentui/react";
import { FaChevronDown, FaMagnifyingGlass } from "react-icons/fa6";
import { toast } from "react-toastify";

import { gloggedinuser } from "../../../interfaces";
import {
    poEditV1Row,
    getPoSourceFid_po_ed_v1,
    getItemPoId_po_ed_v1,
    getItemPoRowIds_po_ed_v1,
    fetchPoChoices_po_ed_v1,
    searchPoChoices_po_ed_v1,
    updatePoMatch_po_ed_v1,
    updatePoMatchMulti_po_ed_v1,
    getPoEditingFields_po_ed_v1,
    patchInvoiceDataAiRecon_po_ed_v1,
    patchInvoiceDataAiReconMulti_po_ed_v1
} from "./poEditV1";

import styles from "./PoEditCellV1.module.css";
import { TbWorldSearch } from "react-icons/tb";
import { RiGlobalOffLine } from "react-icons/ri";
interface PoEditCellV1Props {
    item: any;          // the invoice line item data
    fid: string;        // opened invoice file id (fidState)
    selectedGroup: any;
    loggedInUser: gloggedinuser;
    onApplied: (updatedInvoiceData: any) => void;
    contractorVat: string;
    purchase_order: string;
    selectedItems?: any[]; // invoice lines currently ticked in the table; used to broadcast a PO to all of them
}

// title-case a raw field name for the column header
const labelize = (field: string): string =>
    field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

// build a case-insensitive regex from a search term, treating `*` as a wildcard
const buildSearchPattern = (term: string): string =>
    term.trim().replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");

// render a cell value with the matched phrase highlighted
const highlightMatch = (value: string, term: string): React.ReactNode => {
    if (term.trim().length < 2) return value;
    let re: RegExp;
    try {
        re = new RegExp(`(${buildSearchPattern(term)})`, "ig");
    } catch {
        return value;
    }
    const parts = value.split(re);
    return parts.map((part, i) =>
        part && i % 2 === 1
            ? <mark key={i} className={styles.poSearchHl}>{part}</mark>
            : <span key={i}>{part}</span>
    );
};

const PoEditCellV1: React.FC<PoEditCellV1Props> = ({ item, fid, selectedGroup, loggedInUser, onApplied, contractorVat, purchase_order, selectedItems}) => {
    const cellRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen]         = useState<boolean>(false);
    const [loading, setLoading]       = useState<boolean>(false);
    const [updating, setUpdating]     = useState<boolean>(false);
    const [rows, setRows]             = useState<poEditV1Row[]>([]);
    const [fields, setFields]         = useState<string[]>([]);
    const [loadedOnce, setLoadedOnce] = useState<boolean>(false);
    const [search, setSearch]         = useState<string>("");

    // space-wide PO search: toggled from the icon inside the search field, reuses the same input
    const [globalMode, setGlobalMode]     = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchRows, setSearchRows]     = useState<poEditV1Row[]>([]);
    const [searchFields, setSearchFields] = useState<string[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // filter rows by wildcard search across all shown columns; auto-applies from the 2nd character
    const displayRows = useMemo(() => {
        const t = search.trim();
        if (t.length < 2) return rows;
        let re: RegExp;
        try {
            re = new RegExp(buildSearchPattern(t), "i");
        } catch {
            return rows;
        }
        return rows.filter(row => fields.some(f => re.test(String(row[f] ?? ""))));
    }, [rows, fields, search]);

    // memoized: these builders return fresh arrays/strings each call; unmemoized they retrigger effects every render
    const configFields   = useMemo(() => getPoEditingFields_po_ed_v1(selectedGroup), [selectedGroup]);
    const sourceFid      = useMemo(() => getPoSourceFid_po_ed_v1(selectedGroup), [selectedGroup]);
    const currentPoRowIds = getItemPoRowIds_po_ed_v1(item);                 // Unique Ids of ALL assigned PO lines (primary + multi_assign_pos)
    const hasMatch        = currentPoRowIds.length > 0;                     // Only matched lines have a reconciliation PO row
    const currentPoId     = hasMatch ? getItemPoId_po_ed_v1(item) : "";     // PO Doc Id(s), ';'-joined when multi-assigned. blank when unmatched

    // pending multi-assignment (ordered: first checked = primary). Row objects are kept so selections survive list/global toggling.
    const [pendingMulti, setPendingMulti] = useState<poEditV1Row[]>([]);
    const rowUidOf = (row: poEditV1Row): string => String(row.__po_row_id ?? row.__po_row_idx ?? "").trim();
    const isPending = (row: poEditV1Row): boolean => pendingMulti.some(p => rowUidOf(p) === rowUidOf(row));
    const togglePending = (row: poEditV1Row) => {
        setPendingMulti(prev => prev.some(p => rowUidOf(p) === rowUidOf(row))
            ? prev.filter(p => rowUidOf(p) !== rowUidOf(row))
            : [...prev, row]);
    };

    // fetch choices: show spinner only on the first load; silently refresh afterwards
    const loadChoices = useCallback(async () => {
        if (!loadedOnce) setLoading(true);
        try {
            const res = await fetchPoChoices_po_ed_v1(loggedInUser, sourceFid, configFields, contractorVat, purchase_order);
            if (res.status === "success") {
                setRows(res.rows || []);
                setFields(res.fields || []);
                setLoadedOnce(true);
            }
        } catch {
            toast.error("Could not load PO options");
        } finally {
            setLoading(false);
        }
    }, [loggedInUser, sourceFid, configFields, loadedOnce, contractorVat, purchase_order]);

    const openDropdown = () => {
        setIsOpen(true);
        setSearch("");
        setGlobalMode(false);
        setSearchRows([]);
        setPendingMulti([]);
        loadChoices();
    };

    const spaceId = String(selectedGroup?.selectionId || selectedGroup?.groupid || "");

    const toggleGlobalMode = (e: React.MouseEvent) => {
        e.stopPropagation();
        setGlobalMode(g => !g);
        setSearchRows([]);
        searchInputRef.current?.focus();
    };

    // debounced space-wide search (global mode only): fires only from the 2nd character onwards
    useEffect(() => {
        if (!isOpen || !globalMode) { setSearchLoading(false); return; }
        const term = search.trim();
        if (term.length < 2) { setSearchRows([]); setSearchLoading(false); return; }

        setSearchLoading(true);
        const handle = setTimeout(async () => {
            try {
                const res = await searchPoChoices_po_ed_v1(loggedInUser, sourceFid, configFields, term, spaceId);
                if (res.status === "success") {
                    setSearchRows(res.rows || []);
                    setSearchFields(res.fields || []);
                }
            } catch {
                toast.error("PO search failed");
            } finally {
                setSearchLoading(false);
            }
        }, 400);
        return () => clearTimeout(handle);
    }, [search, isOpen, globalMode, loggedInUser, sourceFid, configFields, spaceId]);


    // unique identifier for an invoice line (mirrors backend matching on item_mapping_number || item_number)
    const invItemIdOf = (it: any): string => String(it?.item_mapping_number ?? it?.item_number ?? "").trim();

    // handle po item reassignment.
    // broadcast rule: when this line is itself part of the table's current selection, the chosen PO is applied
    // to EVERY selected line; otherwise only this line (the one with the open dropdown) is updated — exactly as before.
    const handleSelect = async (row: poEditV1Row) => {
        import.meta.env.DEV && console.log("‹handleSelect_row:›", row);
        import.meta.env.DEV && console.log("‹handleSelect_item:›", item);

        const currentIsSelected = (selectedItems || []).some(s => invItemIdOf(s) === invItemIdOf(item));
        // de-duplicate selected lines by invoice-line id; fall back to just this line when not broadcasting
        const targets = currentIsSelected
            ? Array.from(new Map((selectedItems || []).map(s => [invItemIdOf(s), s])).values())
            : [item];

        setUpdating(true);
        try {
            const activeFields = fields.length > 0 ? fields : configFields;
            let finalInvoiceData: any = null;
            let failCount = 0;
            let sawNewItem = false;

            // sequential: each call persists the full invoicedata, so the last response already contains every update
            for (const target of targets) {
                const res = await updatePoMatch_po_ed_v1(
                    loggedInUser,
                    fid,
                    sourceFid,
                    invItemIdOf(target), // unique identifier for the invoice line
                    row.__po_row_idx,    // unique DB key for chosen PO
                    activeFields
                );
                if (res.status === "success" && res.invoicedata) {
                    finalInvoiceData = res.invoicedata;
                } else {
                    failCount++;
                    if (res?.message === "item_not_found") sawNewItem = true;
                    import.meta.env.DEV && console.log("‹handleSelect_failed_res:›", res);
                }
            }

            if (finalInvoiceData) {
                // patch ai_recon comparison numbers from each live grid item so they update on change, not only after save
                let patched = finalInvoiceData;
                for (const target of targets) patched = patchInvoiceDataAiRecon_po_ed_v1(patched, target, row, activeFields);
                onApplied(patched);
                setIsOpen(false);
                if (failCount > 0) {
                    toast.warn(sawNewItem
                        ? "Some lines were skipped — save the invoice first before assigning POs to new items."
                        : `PO applied — ${failCount} line${failCount === 1 ? "" : "s"} could not be updated.`);
                }
            } else {
                if (sawNewItem) {
                    toast.error("Save Invoice first, before assigning PO to new items.");
                } else {
                    toast.error("Failed to update PO match");
                }
            }
        } catch {
            toast.error("Failed to update PO match");
        } finally {
            setUpdating(false);
        }
    };

    // handle multi po item assignment: first selected → reconciliation, rest → reconciliation.multi_assign_pos
    const handleApplyMulti = async () => {
        if (pendingMulti.length === 0) return;
        if (pendingMulti.length === 1) {
            await handleSelect(pendingMulti[0]); setPendingMulti([]); return;
        }

        setUpdating(true);
        try {
            const activeFields = fields.length > 0 ? fields : configFields;
            const res = await updatePoMatchMulti_po_ed_v1(
                loggedInUser,
                fid,
                sourceFid,
                item?.item_mapping_number || item?.item_number,
                pendingMulti.map(r => r.__po_row_idx),
                activeFields
            );
            if (res.status === "success" && res.invoicedata) {
                const patched = patchInvoiceDataAiReconMulti_po_ed_v1(res.invoicedata, item, pendingMulti, activeFields);
                onApplied(patched);
                setPendingMulti([]);
                setIsOpen(false);
            } else {
                toast.error("Failed to update PO match");
            }
        } catch {
            toast.error("Failed to update PO match");
        } finally {
            setUpdating(false);
        }
    };

    // table source depends on the toggle; nothing is applied until a row is clicked
    const tableFields = globalMode ? searchFields : fields;
    const tableRows   = globalMode ? searchRows : displayRows;
    const gridTemplate = { gridTemplateColumns: `22px repeat(${Math.max(tableFields.length, 1)}, minmax(60px, 1fr))` };

    const renderTable = () => (
        <div className={styles.poListScroll}>
            <div className={styles.poListHeaderRow} style={gridTemplate}>
                <div className={styles.poCellCol} title="Tick to assign several PO lines to this invoice line" />
                {tableFields.map(f => (
                    <div key={f} className={styles.poCellCol}>{labelize(f)}</div>
                ))}
            </div>
            {tableRows.length === 0 ? (
                <div className={styles.poListEmpty}>{globalMode ? "Enter 2 characters to search all POs" : "No pre-recognised PO options available"}</div>
            ) : tableRows.map(row => {
                const rowUid = String(row.__po_row_id ?? "").trim();
                const isSelected = rowUid !== "" && currentPoRowIds.includes(rowUid);
                const pending = isPending(row);
                const pendingPos = pending ? pendingMulti.findIndex(p => rowUidOf(p) === rowUidOf(row)) + 1 : 0;
                return (
                    <div
                        key={rowUid || row.__po_row_idx}
                        className={styles.poListRow + (isSelected ? " " + styles.poListRowSelected : "") + (pending ? " " + styles.poListRowPending : "")}
                        style={gridTemplate}
                        // with a pending multi-selection, row clicks toggle it; otherwise a click assigns this single PO line
                        onClick={() => pendingMulti.length > 0 ? togglePending(row) : handleSelect(row)}
                    >
                        <div className={styles.poCellCheck} onClick={e => { e.stopPropagation(); togglePending(row); }}>
                            <input type="checkbox" checked={pending} readOnly title={pending ? `#${pendingPos}${pendingPos === 1 ? " (primary)" : ""}` : "Add to multi-PO selection"} />
                        </div>
                        {tableFields.map(f => (
                            <div key={f} className={styles.poCellCol} title={String(row[f] ?? "")}>
                                {highlightMatch(String(row[f] ?? ""), search)}
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );

    const renderBody = () => {
        if (globalMode) {
            if (searchLoading) return (
                <div className={styles.poCalloutLoading}>
                    <Spinner size={SpinnerSize.small} />
                    <span>Searching…</span>
                </div>
            );
            if (search.trim().length < 2) return <div className={styles.poListEmpty}>Type at least 2 characters to search all POs</div>;
            return renderTable();
        }
        if (rows.length === 0) return <div className={styles.poListEmpty}>No PO options available</div>;
        return renderTable();
    };

    return (
        <>
            <div
                ref={cellRef}
                className={styles.poCell + (updating ? " " + styles.poCellUpdating : "")}
                onClick={openDropdown}
                title="Click to change the matched PO"
            >
                <span className={styles.poCellValue + (currentPoId ? "" : " " + styles.poCellPlaceholder)} title={currentPoId}>
                    {currentPoId || "Set PO…"}
                </span>
                {currentPoRowIds.length > 1 && (
                    <span className={styles.poCellMultiBadge} title={`${currentPoRowIds.length} PO lines assigned`}>×{currentPoRowIds.length}</span>
                )}
                <span className={styles.poCellCaret}>
                    {updating ? <Spinner size={SpinnerSize.xSmall} /> : <div title="Choose from predetermined POs" className={styles.poCellSearchIcon}> <FaChevronDown size={9} /></div>}
                </span>
            </div>

            {isOpen && (
                <Callout
                    target={cellRef.current}
                    onDismiss={() => setIsOpen(false)}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    gapSpace={2}
                    setInitialFocus
                    styles={{ root: { borderRadius: 6 }, calloutMain: { borderRadius: 6 } }}
                >
                    <div className={styles.poCallout}>
                        {loading ? (
                            <div className={styles.poCalloutLoading}>
                                <Spinner size={SpinnerSize.small} />
                                <span>Loading PO options…</span>
                            </div>
                        ) : (
                            <>
                            <div className={styles.poSearchWrap}>
                                <span
                                    className={styles.poSearchGlobalToggle + (globalMode ? " " + styles.poSearchGlobalToggleOn : "")}
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={toggleGlobalMode}
                                    role="switch"
                                    aria-checked={globalMode}
                                    title={globalMode ? "Global Search ON - searching from all PO IDs in this space" : "Click to search from All PO IDs."}
                                >
                                    {globalMode ? <TbWorldSearch size={14} color="#fff" /> : <RiGlobalOffLine size={17} color="#cf5416" />}
                                </span>
                                <input
                                    ref={searchInputRef}
                                    className={styles.poSearchInput + (globalMode ? " " + styles.poSearchInputGlobal : "")}
                                    type="text"
                                    value={search}
                                    autoFocus
                                    placeholder={globalMode ? "Search all PO IDs in this space (min 2 characters)" : "Search List by PO ID"}
                                    onChange={e => setSearch(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                />

                            </div>
                            {renderBody()}
                            {pendingMulti.length > 0 && (
                                <div className={styles.poMultiFooter}>
                                    <span>{pendingMulti.length} PO line{pendingMulti.length > 1 ? "s" : ""} selected (first = primary)</span>
                                    <button type="button" className={styles.poMultiBtnGhost} onClick={() => setPendingMulti([])} disabled={updating}>Clear</button>
                                    <button type="button" className={styles.poMultiBtn} onClick={handleApplyMulti} disabled={updating}>Assign</button>
                                </div>
                            )}
                            </>
                        )}
                    </div>
                </Callout>
            )}
        </>
    );
};

export default PoEditCellV1;
